'use client';

// src/components/cart/cart-context.tsx

import React, {
 createContext,
 useContext,
 useEffect,
 useMemo,
 useReducer,
 useState,
} from 'react';
import type { DigitalBookFormat } from '@/lib/platform/storefront-checkout';

export type CartItem = {
 id: number;
 slug: string;
 title: string;
 price: number;
 quantity: number;
 currency?: 'EUR' | 'USD';
 coverUrl?: string;
 coverAlt?: string;
 routeHref?: string;
 itemType?: 'title' | 'bundle';
 collectionId?: string;
 includedTitleIds?: string[];
 digitalFormat?: DigitalBookFormat;
 digitalSaleState?: 'available' | 'coming-soon' | 'preorder';
 availableDigitalFormats?: DigitalBookFormat[];
};

type State = { items: CartItem[] };

type Add = { type: 'ADD'; item: CartItem };
type Remove = { type: 'REMOVE'; id: number };
type SetQty = { type: 'SET_QTY'; id: number; quantity: number };
type SetFormat = { type: 'SET_FORMAT'; id: number; digitalFormat: DigitalBookFormat };
type Clear = { type: 'CLEAR' };
type Hydrate = { type: 'HYDRATE'; items: CartItem[] };

type Action = Add | Remove | SetQty | SetFormat | Clear | Hydrate;

const STORAGE_KEY = 'cart:v1';

const CartContext = createContext<
 { state: State; dispatch: React.Dispatch<Action>; hydrated: boolean } | undefined
>(undefined);

function reducer(state: State, action: Action): State {
 switch (action.type) {
 case 'HYDRATE':
 return { items: action.items.map((item) => ({ ...item, quantity: item.itemType === 'title' || item.itemType === 'bundle' ? 1 : Math.max(1, item.quantity || 1) })) };
 case 'ADD': {
 const existing = state.items.find((i) => i.id === action.item.id);
 if (existing) {
 return {
 items: state.items.map((i) =>
 i.id === action.item.id
 ? { ...i, ...action.item, quantity: action.item.itemType === 'bundle' || action.item.itemType === 'title' ? 1 : i.quantity + action.item.quantity }
 : i
 ),
 };
 }
 return { items: [...state.items, action.item] };
 }
 case 'REMOVE':
 return { items: state.items.filter((i) => i.id !== action.id) };
 case 'SET_QTY':
 return {
 items: state.items.map((i) =>
 i.id === action.id
 ? { ...i, quantity: Math.max(1, action.quantity) }
 : i
 ),
 };
 case 'SET_FORMAT':
 return {
 items: state.items.map((i) => i.id === action.id ? { ...i, digitalFormat: action.digitalFormat } : i),
 };
 case 'CLEAR':
 return { items: [] };
 default:
 return state;
 }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
 const [state, dispatch] = useReducer(reducer, { items: [] });
 const [hydrated, setHydrated] = useState(false);

 useEffect(() => {
 try {
 const raw = localStorage.getItem(STORAGE_KEY);
 const stored = raw ? (JSON.parse(raw) as Partial<State>) : null;
 const items = Array.isArray(stored?.items)
 ? stored.items.filter((item): item is CartItem => Boolean(item && typeof item === 'object' && typeof (item as CartItem).slug === 'string'))
 : [];
 dispatch({ type: 'HYDRATE', items });
 } catch {
 dispatch({ type: 'HYDRATE', items: [] });
 } finally {
 setHydrated(true);
 }
 }, []);

 useEffect(() => {
 if (!hydrated) return;
 try {
 localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
 } catch {
 // ignore storage errors
 }
 }, [hydrated, state]);

 const value = useMemo(() => ({ state, dispatch, hydrated }), [hydrated, state]);
 return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
 const ctx = useContext(CartContext);
 if (!ctx) throw new Error('useCart must be used within CartProvider');
 return ctx;
}

/** Convenience selector for checkout & summaries */
export function useCartItems() {
 return useCart().state.items;
}

/** Private previews never hydrate, persist or mutate the customer cart. */
export function PreviewCartProvider({ children }: { children: React.ReactNode }) {
 return <CartContext.Provider value={{ state: { items: [] }, dispatch: () => undefined, hydrated: true }}>{children}</CartContext.Provider>;
}
